import { type FeedSort, GENRE_NAMES } from '@mogzo/shared';
import { ArrowUpRight } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { fetchPosts, type Post, timeAgo } from '../../lib/communities';
import type { GameCard } from '../../lib/games';
import { useSession } from '../../lib/session';
import { Composer, SORTS } from '../../pages/community/TestCommunity';
import { GameCover, NewChip } from '../games/GameArt';
import { Alert } from '../ui';
import { Avatar } from './Avatar';
import { GgButton } from './GgButton';
import { AuthorName, actionClass, CommentIcon, commentsLabel, TagChip } from './PostCard';
import { PostImages } from './PostImages';

/** A community's posts in one sort, a page at a time; null while the first page loads. */
export function useCommunityPosts(communityId: string | null, sort: FeedSort) {
  const [posts, setPosts] = useState<Post[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [failed, setFailed] = useState(false);
  const loading = useRef(false);

  useEffect(() => {
    if (!communityId) return;
    let active = true;
    setPosts(null);
    setFailed(false);
    fetchPosts(communityId, sort)
      .then((page) => {
        if (!active) return;
        setPosts(page.posts);
        setHasMore(page.hasMore);
      })
      .catch(() => active && setFailed(true));
    return () => {
      active = false;
    };
  }, [communityId, sort]);

  async function loadMore() {
    if (!posts || !communityId || loading.current) return;
    loading.current = true;
    try {
      const page = await fetchPosts(communityId, sort, posts.length);
      setPosts([...posts, ...page.posts.filter((p) => !posts.some((q) => q.id === p.id))]);
      setHasMore(page.hasMore);
    } catch {
      setFailed(true);
    } finally {
      loading.current = false;
    }
  }

  return { posts, setPosts, hasMore, failed, loadMore };
}

/** In place of the posts for someone signed out. */
export function SignInForPosts({ next }: { next: string }) {
  return (
    <p className="py-6 text-center text-sm text-muted">
      <Link
        to={`/sign-in?next=${encodeURIComponent(next)}`}
        className="font-semibold text-ink underline decoration-brand decoration-2 underline-offset-4"
      >
        Sign in
      </Link>{' '}
      to see what players are posting.
    </p>
  );
}

/**
 * A community's posts beside a game on its play page: sorts on top, and posts that scroll on
 * their own, loading older ones as the list reaches its end.
 */
export function PostsPanel({
  communityId,
  path,
  posting,
  placeholder,
  back,
  games = [],
}: {
  /** The play page's Back, at the panel's top on wide screens. */
  back?: ReactNode;
  /** Other games, shown in blocks between the posts (live games only). */
  games?: GameCard[];
  communityId: string | null;
  /** Where the community lives: posts open at `<path>/posts/<id>`. */
  path: string;
  /** `open`: the viewer can post here. `later`: posting opens at the launch. */
  posting: 'open' | 'later' | 'none';
  placeholder: string;
}) {
  const { me } = useSession();
  const [sort, setSort] = useState<FeedSort>('hot');
  // Posts are for people signed in; someone signed out is asked to sign in instead.
  const readable = Boolean(communityId && me);
  const { posts, setPosts, hasMore, failed, loadMore } = useCommunityPosts(
    readable ? communityId : null,
    sort,
  );
  const composer = useRef<HTMLTextAreaElement>(null);
  const more = useRef<HTMLButtonElement>(null);

  // Older posts load as the list reaches its end; the button is there for keyboards too.
  useEffect(() => {
    const end = more.current;
    if (!end || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) void loadMore();
    });
    observer.observe(end);
    return () => observer.disconnect();
  }, [loadMore]);

  // Posts with blocks of games between them, like the home feed.
  const blocks: GameCard[][] = [];
  for (let i = 0; i < games.length; i += GAMES_PER_BLOCK) {
    blocks.push(games.slice(i, i + GAMES_PER_BLOCK));
  }
  const items: ReactNode[] = [];
  let shownBlocks = 0;
  for (const [i, post] of (posts ?? []).entries()) {
    items.push(<PanelPost key={post.id} post={post} path={path} />);
    const block = blocks[shownBlocks];
    if (block && blockAfter(i + 1)) {
      items.push(<GamesBlock key={`games-${shownBlocks}`} id={shownBlocks} games={block} />);
      shownBlocks++;
    }
  }
  const loading = readable && !posts && !failed;
  // Too few posts for the first block's place (or none, or signed out): it goes after them.
  const trailingBlock = !loading && shownBlocks === 0 ? blocks[0] : undefined;

  return (
    <aside
      aria-labelledby="posts-heading"
      className="flex flex-col overflow-hidden rounded-2xl border border-line bg-card lg:min-h-0 lg:max-w-[560px] lg:min-w-[380px] lg:flex-1"
    >
      <div className="shrink-0 border-b border-divider px-4 pt-3 pb-3.5">
        <div className="flex min-h-10 items-center gap-3">
          {back}
          <Link
            to={path}
            className="ml-auto inline-flex min-h-10 items-center gap-1 text-sm font-semibold text-muted hover:text-ink"
          >
            Open the community
            <ArrowUpRight size={16} strokeWidth={2.2} aria-hidden="true" />
          </Link>
        </div>
        <div className="mt-2.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <h2 id="posts-heading" className="text-lg leading-6 font-bold tracking-[-0.01em]">
            Posts
          </h2>
          {communityId && (
            <fieldset className="m-0 flex min-w-0 rounded-full border-0 bg-field p-1">
              <legend className="sr-only">Sort posts</legend>
              {SORTS.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  aria-pressed={sort === s.value}
                  onClick={() => setSort(s.value)}
                  className={`h-8 rounded-full px-3 text-[13px] font-semibold ${sort === s.value ? 'bg-card text-ink shadow-[0_1px_2px_rgba(27,21,47,0.1)]' : 'text-muted hover:text-ink'}`}
                >
                  {s.label}
                </button>
              ))}
            </fieldset>
          )}
        </div>
      </div>

      <div className="flex flex-col px-4 pt-3 pb-4 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
        {communityId && me && posting === 'later' && (
          <p className="rounded-[14px] bg-field px-3.5 py-2.5 text-sm text-muted">
            Posting opens when MOGZO launches.
          </p>
        )}
        {communityId && me && posting === 'open' && (
          <Composer
            ref={composer}
            communityId={communityId}
            viewerName={me.displayName}
            placeholder={placeholder}
            open
            onPosted={(post) => setPosts((current) => [post, ...(current ?? [])])}
          />
        )}
        {failed && (
          <div className="mt-3">
            <Alert>Couldn’t load posts. Reload the page to try again.</Alert>
          </div>
        )}
        {communityId && !me && <SignInForPosts next={path} />}
        {loading && (
          <p role="status" className="py-6 text-center text-sm text-muted">
            Loading posts…
          </p>
        )}
        {(!communityId || posts?.length === 0) && (
          <p className="border-b border-divider py-8 text-center text-sm text-muted">
            No posts yet.
          </p>
        )}
        {items}
        {trailingBlock && <GamesBlock id={0} games={trailingBlock} />}
        {hasMore && (
          <button
            ref={more}
            type="button"
            onClick={loadMore}
            className="mt-2 min-h-10 rounded-full text-sm font-semibold text-muted hover:text-ink"
          >
            Show more posts
          </button>
        )}
      </div>
    </aside>
  );
}

/** Games come four at a time: after the second post, then after every six more. */
const GAMES_PER_BLOCK = 4;
const FIRST_BLOCK_AFTER = 2;
const BLOCK_EVERY = 6;
const blockAfter = (shown: number) =>
  shown === FIRST_BLOCK_AFTER ||
  (shown > FIRST_BLOCK_AFTER && (shown - FIRST_BLOCK_AFTER) % BLOCK_EVERY === 0);

/** A block of other games between the posts, two to a row. */
function GamesBlock({ id, games }: { id: number; games: GameCard[] }) {
  const heading = `more-games-${id}`;
  return (
    <section aria-labelledby={heading} className="border-b border-divider py-4">
      <div className="flex items-baseline justify-between gap-3">
        <h3 id={heading} className="text-base leading-[22px] font-bold">
          More games
        </h3>
        <Link
          to="/discover"
          className="text-sm font-semibold underline decoration-brand decoration-2 underline-offset-4"
        >
          Discover
        </Link>
      </div>
      <ul className="mt-3 grid grid-cols-2 gap-x-3 gap-y-4">
        {games.map((game) => (
          <li key={game.id} className="min-w-0">
            <Link to={game.path} className="group block">
              <GameCover game={game} className="aspect-video rounded-xl">
                {game.isNew && (
                  <span className="absolute top-2 left-2">
                    <NewChip />
                  </span>
                )}
              </GameCover>
              <span className="mt-2 block truncate text-sm font-semibold group-hover:underline">
                {game.name}
              </span>
              <span className="block truncate text-[13px] leading-[18px] text-muted">
                {game.genre ? GENRE_NAMES[game.genre] : `by ${game.developer || 'its developer'}`}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** A post, compact for the panel beside the game. */
function PanelPost({ post, path }: { post: Post; path: string }) {
  const to = `${path}/posts/${post.id}`;
  return (
    <article className="flex flex-col gap-2 border-b border-divider py-3.5">
      <div className="flex items-center gap-2.5">
        <Avatar name={post.author.name} size="sm" />
        <div className="min-w-0 flex-1 leading-tight">
          <div className="flex flex-wrap items-center gap-1.5 text-sm">
            <AuthorName author={post.author} />
          </div>
          <span className="text-[13px] text-muted">
            {timeAgo(post.createdAt)}
            {post.build && ` · build #${post.build.number}`}
          </span>
        </div>
        {post.pinned && <span className="text-xs font-bold text-muted">Pinned</span>}
        {post.tag && <TagChip tag={post.tag} />}
      </div>
      {(post.title || post.body) && (
        <Link to={to} className="block">
          {post.title && <h3 className="text-[15px] leading-[21px] font-bold">{post.title}</h3>}
          <p className="line-clamp-3 text-sm leading-5 whitespace-pre-line text-plum">
            {post.body}
          </p>
        </Link>
      )}
      {post.images.length > 0 && (
        <PostImages images={post.images} author={post.author.name} to={to} />
      )}
      <div className="-ml-1.5 flex items-center gap-1">
        <GgButton target="post" id={post.id} count={post.ggCount} on={post.viewerGg} compact />
        <Link to={to} className={actionClass}>
          <CommentIcon />
          {commentsLabel(post.commentCount)}
        </Link>
      </div>
    </article>
  );
}
